import {
  Alert, AlertTitle, IconButton, Snackbar, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePrevious } from '../../reactHelper';
import { errorsActions } from '../../store';

/**
 * What the product says when something failed.
 *
 * This was a bare bar with the server's sentence dropped into it, which read as a label rather
 * than as the product telling somebody their work had not gone through. The sentence is the same;
 * what it needed was a line saying what kind of thing had happened, so it can be taken in at a
 * glance and ignored or acted on, and a way out that is visibly a way out.
 *
 * It sits where this product's other alert sits - top right - so the two cannot arrive in the same
 * place at once, and so neither lands on the bottom menu a phone keeps there.
 */
const ErrorHandler = () => {
  const dispatch = useDispatch();

  const error = useSelector((state) => state.errors.errors.find(() => true));
  // Held while the bar slides out, so the words do not vanish a moment before the bar does.
  const previousError = usePrevious(error);
  const close = () => dispatch(errorsActions.pop());

  return (
    <Snackbar
      open={!!error}
      // A snackbar goes on any click on the page unless it is told otherwise, which would mean the
      // next keystroke of whatever somebody was typing took the message away before they read it.
      // A failure waits to be dismissed: it is the only record they have that it happened.
      onClose={(event, reason) => { if (reason !== 'clickaway') close(); }}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Alert
        severity="error"
        // An alert keeps its own close control only until something is put in its action slot, so
        // the way out is given here rather than lost.
        action={<IconButton size="small" color="inherit" aria-label="Dismiss" onClick={close}><CloseIcon fontSize="small" /></IconButton>}
        sx={{
          maxWidth: 420,
          alignItems: 'flex-start',
          boxShadow: 6,
          '& .MuiAlert-message': { minWidth: 0, overflowWrap: 'anywhere' },
        }}
      >
        <AlertTitle sx={{ mb: 0.25, fontWeight: 700 }}>That did not go through</AlertTitle>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>{error || previousError}</Typography>
      </Alert>
    </Snackbar>
  );
};

export default ErrorHandler;
